import type { FC } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ComparisonScreen } from "@components/ModelTesting/ComparisonScreen";
import { ModelTestingTab } from "@components/ModelTesting/ModelTestingTab";
import { MultiModelTestScreen } from "@components/ModelTesting/MultiModelTestScreen";
import { ModelTestingView } from "@components/ModelTesting/constants";

const Shell: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-full flex-col px-16 pb-16 pt-8">
    <div className="flex-1">{children}</div>
  </div>
);

/** Standalone Model Testing page (formerly a tab on /eval-results). */
export const ModelTesting: FC = () => (
  <Shell>
    <ModelTestingTab />
  </Shell>
);

/** /model-testing/multi/:testId — refresh-safe test detail. */
export const ModelTestingMultiTest: FC = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const id = Number(testId);
  if (!Number.isFinite(id)) return <ModelTesting />;
  return (
    <Shell>
      <MultiModelTestScreen
        testId={id}
        // Results pages belong to the multi-model tab — land back on it.
        onBack={() => navigate(`/model-testing?tab=${ModelTestingView.Multi}`)}
        onOpenComparison={(comparisonId, promptKey) =>
          navigate(
            `/model-testing/comparisons/${comparisonId}?multi=${id}${
              promptKey ? `&focus=${encodeURIComponent(promptKey)}` : ""
            }`,
          )
        }
      />
    </Shell>
  );
};

/** /model-testing/comparisons/:comparisonId(?focus=prompt_key) —
 *  refresh-safe comparison detail; back goes to the owning test when
 *  there is one (the payload carries multi_model_test_id). */
export const ModelTestingComparison: FC = () => {
  const { comparisonId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = Number(comparisonId);
  if (!Number.isFinite(id)) return <ModelTesting />;
  return (
    <Shell>
      <ComparisonScreen
        comparisonId={id}
        focusPromptKey={searchParams.get("focus") ?? undefined}
        multiTestId={searchParams.get("multi") ? Number(searchParams.get("multi")) : undefined}
        onBack={(testId, candidateModel) =>
          navigate(
            testId != null
              ? `/model-testing/multi/${testId}${candidateModel ? `?model=${encodeURIComponent(candidateModel)}` : ""}`
              : "/model-testing",
          )
        }
      />
    </Shell>
  );
};
